<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStrainStore } from '@core/stores/StrainStore'
import { useEquipmentStore } from '@core/stores/EquipmentStore'
import { useSupplyStore } from '@core/stores/SupplyStore'
import { useToolStore } from '@core/stores/ToolStore'
import { useAuthStore } from '@core/stores/AuthStore'
import Step1 from '@/views/setupWizard/Step1.vue'
import Step2 from '@/views/setupWizard/Step2.vue'
import Step3 from '@/views/setupWizard/Step3.vue'
import Step4 from '@/views/setupWizard/Step4.vue'
import Step5 from '@/views/setupWizard/Step5.vue'
import Step6 from '@/views/setupWizard/Step6.vue'

const { t } = useI18n()

definePage({
  meta: {
    layout: 'default',
  },
})

const currentStep = ref(0)
const isCurrentStepValid = ref(true)

const authStore = useAuthStore()

const isAuthenticated = authStore.isAuthenticated

const wizardSteps = computed(() => {
  return [
    {
      title: t('Profile'),
      icon: 'tabler-password-user',
    },
    {
      title: t('Seeds'),
      icon: 'tabler-plant-2',
    },
    {
      title: t('Equipment'),
      icon: 'tabler-trowel',
    },
    {
      title: t('Environments'),
      icon: 'tabler-home-eco',
    },
    {
      title: t('Growcycles'),
      icon: 'tabler-arrow-capsule',
    },
  ].concat(!isAuthenticated ? [
    {
      title: t('Account'),
      icon: 'tabler-user',
    },
  ] : [])
})

const wizardStepsData = ref({
  step1: {
    userExperience: 'firstTimer',
  },
  step3: {},
  step4: {
    budget: null,
  },
  step7: {},
  step8: {},
  step9: {},
})

const nextStep = () => {
  currentStep.value++
}

const previousStep = () => {
  currentStep.value--
}

const isFirstTimer = ref(wizardStepsData.value.step1.userExperience === 'firstTimer')

watch(wizardStepsData.value.step1, value => {
  isFirstTimer.value = value.userExperience === 'firstTimer'
})

/**
 * @param {BeforeUnloadEvent} event
 */
function handleWindowBeforeUnloadEvent(event) {
  event.preventDefault()
  event.returnValue = true
}

onMounted(() => {
  const toolStore = useToolStore()
  const strainStore = useStrainStore()
  const supplyStore = useSupplyStore()
  const equipmentStore = useEquipmentStore()

  strainStore.removeSelectedItemsFromBackend()
  equipmentStore.removeSelectedItemsFromBackend()
  supplyStore.removeSelectedItemsFromBackend()
  toolStore.removeSelectedItemsFromBackend()

  window.addEventListener('beforeunload', handleWindowBeforeUnloadEvent)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleWindowBeforeUnloadEvent)
})
</script>


<template>
  <VCard>
    <VCardText>
      <h1>{{ $t('Setup wizard') }}</h1>
      <!-- 👉 Stepper -->
      <AppStepper
        v-model:current-step="currentStep"
        class="checkout-stepper"
        :items="wizardSteps"
        direction="horizontal"
        :is-active-step-valid="isCurrentStepValid"
        icon-size="36"
        align="center"
      />
    </VCardText>

    <VDivider />

    <VCardText>
      <!-- 👉 stepper content -->
      <VWindow
        v-model="currentStep"
        class="disable-tab-transition"
        :touch="false"
      >
        <VWindowItem>
          <Step1
            v-model:form-data="wizardStepsData.step1"
            v-model:isCurrentStepValid="isCurrentStepValid"
            @next-step="nextStep"
            @previous-step="previousStep"
          />
        </VWindowItem>
        <VWindowItem>
          <Step2
            v-model:form-data="wizardStepsData.step3"
            v-model:isCurrentStepValid="isCurrentStepValid"
            :is-first-timer="isFirstTimer"
            @next-step="nextStep"
            @previous-step="previousStep"
          />
        </VWindowItem>
        <VWindowItem>
          <Step3
            v-model:form-data="wizardStepsData.step4"
            v-model:isCurrentStepValid="isCurrentStepValid"
            @next-step="nextStep"
            @previous-step="previousStep"
          />
        </VWindowItem>
        <VWindowItem>
          <Step4
            v-model:form-data="wizardStepsData.step7"
            v-model:isCurrentStepValid="isCurrentStepValid"
            @next-step="nextStep"
            @previous-step="previousStep"
          />
        </VWindowItem>
        <VWindowItem>
          <Step5
            v-model:form-data="wizardStepsData.step8"
            v-model:isCurrentStepValid="isCurrentStepValid"
            @next-step="nextStep"
            @previous-step="previousStep"
          />
        </VWindowItem>
        <VWindowItem v-if="!isAuthenticated">
          <Step6
            v-model:form-data="wizardStepsData.step9"
            v-model:is-current-step-valid="isCurrentStepValid"
            @previous-step="previousStep"
          />
        </VWindowItem>
      </VWindow>
    </VCardText>
  </VCard>
</template>
